import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { FESTIVOS_2026, diaLab, diasLab, fecha } from "./calendario";

const SHEET = "FE Proyectos";
const TABLE = "ProyectosFE";
const CORTE = fecha(2026, 6, 30);
const FESTIVOS = FESTIVOS_2026.map(([f]) => f);
const SOLO_DOMINGO = "0000001";
const MAR_MIE = "0110000";

const PROYECTOS = [
  "Migración de servidores", "Auditoría interna", "Lanzamiento de catálogo", "Capacitación de ventas",
  "Inventario anual", "Rediseño del sitio web", "Implementación de ERP", "Cierre fiscal",
  "Campaña de verano", "Mudanza de bodega", "Certificación ISO", "Integración de CRM",
] as const;

const rnd = seededRandom(4004);
// Proyecto, Inicio, Fin, Días de trabajo
const ROWS = Array.from({ length: 24 }, (_, i) => {
  const inicio = fecha(2026, 1, 5) + rnd.int(0, 300);
  return [
    `${PROYECTOS[i % PROYECTOS.length]}${i >= PROYECTOS.length ? " II" : ""}`,
    inicio,
    inicio + rnd.int(10, 120),
    rnd.int(5, 60),
  ] as [string, number, number, number];
}).sort((a, b) => a[1] - b[1]);

type Row = (typeof ROWS)[number];
const habiles = (r: Row) => diasLab(r[1], r[2], FESTIVOS);
const avance = (r: Row) => Math.max(0, Math.min(1, diasLab(r[1], CORTE, FESTIVOS) / habiles(r)));

export const spec: FuncionProjectSpec = {
  projectId: "funciones-dias-habiles",
  sheets: [
    {
      name: SHEET,
      title: "Proyectos y entregas",
      subtitle: "Calendario de proyectos 2026 — con los días festivos oficiales de México",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Proyecto", "Inicio", "Fin", "Días de trabajo"],
          rows: ROWS,
          answerColumns: [
            "Días naturales",
            "Días hábiles",
            "Hábiles sin festivos",
            "Entrega",
            "Hábiles L-S",
            "Entrega turno especial",
            "Avance",
          ],
          formats: {
            Inicio: "dd/mm/yyyy",
            Fin: "dd/mm/yyyy",
            Entrega: "ddd dd/mm/yyyy",
            "Entrega turno especial": "ddd dd/mm/yyyy",
            Avance: "0%",
          },
        },
        {
          name: "Festivos",
          anchor: "N4",
          headers: ["Fecha", "Festivo"],
          rows: FESTIVOS_2026,
          formats: { Fecha: "dd/mm/yyyy" },
        },
      ],
      inputs: [{ address: "O14", label: "Fecha de corte", value: CORTE, format: "dd/mm/yyyy" }],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Días naturales", fns: ["DAYS"],
      expected: ROWS.map((r) => r[2] - r[1] + 1),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Días hábiles", fns: ["NETWORKDAYS"],
      expected: ROWS.map((r) => diasLab(r[1], r[2])),
    },
    3: { kind: "column", sheet: SHEET, table: TABLE, column: "Hábiles sin festivos", fns: ["NETWORKDAYS"], expected: ROWS.map(habiles) },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Entrega", fns: ["WORKDAY"],
      expected: ROWS.map((r) => diaLab(r[1], r[3], FESTIVOS)),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Hábiles L-S", fns: ["NETWORKDAYS.INTL"],
      expected: ROWS.map((r) => diasLab(r[1], r[2], FESTIVOS, SOLO_DOMINGO)),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Entrega turno especial", fns: ["WORKDAY.INTL"],
      expected: ROWS.map((r) => diaLab(r[1], r[3], FESTIVOS, MAR_MIE)),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Avance", fns: ["NETWORKDAYS", "MIN", "MAX"],
      expected: ROWS.map(avance),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;
const AVANCE_FORMULA =
  "=MAX(0,MIN(1,DIAS.LAB([@Inicio],$O$14,Festivos[Fecha])/DIAS.LAB([@Inicio],[@Fin],Festivos[Fecha])))";

export const project: ProjectDefinition = {
  id: "funciones-dias-habiles",
  title: "DIAS.LAB y DIA.LAB: Proyectos y entregas",
  subtitle: "Contar y sumar días hábiles con festivos",
  description:
    "Planeas 24 proyectos de 2026. Para cada uno necesitas cuántos días hábiles dura, en qué fecha se entrega si toma cierto número de días de trabajo, cómo cambian las fechas si el equipo trabaja sábados o descansa martes y miércoles, y qué porcentaje lleva al día de corte.\n\nLa tabla Festivos tiene los días de descanso obligatorio de México en 2026 (LFT, artículo 74).",
  topics: [
    "Días naturales con DIAS",
    "DIAS.LAB: contar días hábiles",
    "Excluir festivos con una tabla",
    "DIA.LAB: fecha después de N días hábiles",
    "Fines de semana personalizados con .INTL",
    "Porcentaje de avance acotado con MIN y MAX",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Días naturales",
      description:
        "En la hoja <strong>FE Proyectos</strong>, columna <strong>Días naturales</strong>: cuántos días de calendario dura cada proyecto, <strong>contando el día de inicio y el de fin</strong>.",
      solution: [
        `Selecciona ${F("E5")}.`,
        "DIAS(fin, inicio) resta las fechas, pero no cuenta el día de inicio. Súmale 1.",
        `Escribe ${F("=DIAS([@Fin],[@Inicio])+1")}.`,
      ],
      expectedResult: "Días de calendario",
      finalFormula: "=DIAS([@Fin],[@Inicio])+1",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Días hábiles",
      description: "En la columna <strong>Días hábiles</strong>: cuántos días de <strong>lunes a viernes</strong> hay entre Inicio y Fin (incluidos).",
      solution: [
        `Selecciona ${F("F5")}.`,
        "DIAS.LAB cuenta los días de lunes a viernes, incluyendo el inicio y el fin.",
        `Escribe ${F("=DIAS.LAB([@Inicio],[@Fin])")}.`,
      ],
      expectedResult: "Días hábiles sin quitar festivos",
      finalFormula: "=DIAS.LAB([@Inicio],[@Fin])",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Sin días festivos",
      description: "En la columna <strong>Hábiles sin festivos</strong>: igual que la Tarea 2, pero sin contar los días de la tabla <strong>Festivos</strong> (N4).",
      solution: [
        `Selecciona ${F("G5")}.`,
        "El tercer argumento de DIAS.LAB es la lista de festivos.",
        `Escribe ${F("=DIAS.LAB([@Inicio],[@Fin],Festivos[Fecha])")}.`,
        "Compara con la Tarea 2: los proyectos que cruzan el 16 de septiembre o el 16 de noviembre tienen un día menos.",
      ],
      expectedResult: "Días hábiles reales",
      finalFormula: "=DIAS.LAB([@Inicio],[@Fin],Festivos[Fecha])",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Fecha de entrega",
      description:
        "En la columna <strong>Entrega</strong>: la fecha en que termina el proyecto si empieza en <strong>Inicio</strong> y toma los <strong>Días de trabajo</strong> indicados (hábiles, sin festivos).",
      solution: [
        `Selecciona ${F("H5")}.`,
        "DIA.LAB(inicio, días, festivos) avanza esos días hábiles. No cuenta el propio día de inicio.",
        `Escribe ${F("=DIA.LAB([@Inicio],[@[Días de trabajo]],Festivos[Fecha])")}.`,
      ],
      expectedResult: "Siempre de lunes a viernes y nunca en festivo",
      finalFormula: "=DIA.LAB([@Inicio],[@[Días de trabajo]],Festivos[Fecha])",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Equipo que trabaja sábados",
      description:
        "En la columna <strong>Hábiles L-S</strong>: un equipo trabaja de <strong>lunes a sábado</strong> y solo descansa el domingo. Cuenta sus días hábiles entre Inicio y Fin, sin festivos.",
      solution: [
        `Selecciona ${F("I5")}.`,
        `DIAS.LAB.INTL agrega un argumento de fin de semana. El código ${F("11")} significa \"solo domingo\".`,
        `Escribe ${F("=DIAS.LAB.INTL([@Inicio],[@Fin],11,Festivos[Fecha])")}.`,
      ],
      expectedResult: "Más días que en la Tarea 3",
      finalFormula: "=DIAS.LAB.INTL([@Inicio],[@Fin],11,Festivos[Fecha])",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Turno con descanso martes y miércoles",
      description: conditions(
        "En la columna <strong>Entrega turno especial</strong>: un equipo de soporte descansa <strong>martes y miércoles</strong> y trabaja el resto, incluso sábado y domingo. Calcula su fecha de entrega con los mismos Días de trabajo y sin festivos.",
        [
          "Ningún código numérico cubre martes + miércoles",
          "Usa una <strong>cadena de 7 caracteres</strong> de lunes a domingo: <strong>1</strong> = descanso, <strong>0</strong> = trabajo",
          "Martes y miércoles de descanso → <strong>\"0110000\"</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("J5")}.`,
        `Escribe ${F('=DIA.LAB.INTL([@Inicio],[@[Días de trabajo]],"0110000",Festivos[Fecha])')}.`,
        "Revisa que ninguna entrega caiga en martes o miércoles.",
      ],
      expectedResult: "Entregas que nunca caen en martes ni miércoles",
      finalFormula: '=DIA.LAB.INTL([@Inicio],[@[Días de trabajo]],"0110000",Festivos[Fecha])',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Porcentaje de avance",
      description: conditions(
        "En la columna <strong>Avance</strong>: qué porcentaje del proyecto lleva al día de corte (<strong>O14</strong>), medido en días hábiles sin festivos:",
        [
          "Arriba: días hábiles del <strong>Inicio</strong> al <strong>corte</strong>",
          "Abajo: días hábiles del <strong>Inicio</strong> al <strong>Fin</strong>",
          "El resultado debe quedar entre <strong>0%</strong> (aún no empieza) y <strong>100%</strong> (ya terminó): usa <strong>MIN</strong> y <strong>MAX</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        "Si el proyecto empieza después del corte, la división da negativa: MAX(0, …) la vuelve 0. Si ya terminó, da más de 1: MIN(1, …) la deja en 100%.",
        `Escribe ${F(AVANCE_FORMULA)}.`,
      ],
      expectedResult: "0% a 100%",
      finalFormula: AVANCE_FORMULA,
    },
  ],
};
